/**
 * FinanceApp - Copyright (c) 2026 Emmanuel TESTE
 * Tous droits réservés - Logiciel propriétaire
 */
import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const S = {
  card:  { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn:   { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
};

// ── Modules réels du forfait souscrit (chantier "3 forfaits") ────────────
// Plus de catalogue à la carte : le contenu vient du forfait
// (server/config/plans.js), affiché ici en lecture seule. Le changement de
// forfait se fait sur l'écran "Mon abonnement", jamais module par module.
const MODULE_CATALOG = {
  crm:         { ico: "🤝", name: "CRM & Prospection",          desc: "Contacts, opportunités, pipeline commercial, activités, rapports d'équipe. Inclut l'agent de prospection IA (génération de leads, emails personnalisés)." },
  planning:    { ico: "📅", name: "Planning & RDV",             desc: "Agenda en ligne, prise de RDV client, types de rendez-vous, rappels automatiques." },
  abonnements: { ico: "🔁", name: "Abonnements récurrents",     desc: "Gestion des abonnements clients et fournisseurs, facturation automatique, connexion à votre propre plateforme emailing." },
  stocks:      { ico: "📦", name: "Stocks & Commandes",         desc: "Multi-entrepôts, FIFO/CMUP, lots & séries, commandes fournisseurs, alertes de seuil." },
  ged:         { ico: "🗂️", name: "Espace documentaire (GED)",  desc: "Archivage légal 10 ans, versioning, rappels d'échéance, liens avec factures et contrats." },
  analytique:  { ico: "📊", name: "Comptabilité analytique",    desc: "Axes et codes analytiques, budget vs réel, rapports P&L par axe, affectation automatique." },
  budget:      { ico: "📈", name: "Budget & Prévisionnel",      desc: "Multi-scénarios, point mort, cash-flow N+36, analyse IA, comparaison inter-scénarios." },
  groupe:      { ico: "🏢", name: "Vue Groupe & Consolidation", desc: "Trésorerie et impayés consolidés sur toutes les sociétés de votre portefeuille." },
};

// ── Champs personnalisés — constantes ────────────────────────────────────
const ENTITY_TYPES = [
  { id: "facture",       label: "Factures" },
  { id: "devis",         label: "Devis" },
  { id: "bon_commande",  label: "Bons de commande" },
  { id: "bon_livraison", label: "Bons de livraison" },
  { id: "client",        label: "Clients" },
  { id: "produit",       label: "Produits" },
];

const FIELD_TYPES = [
  { id: "text",     label: "Texte court" },
  { id: "textarea", label: "Texte long" },
  { id: "number",   label: "Nombre" },
  { id: "date",     label: "Date" },
  { id: "select",   label: "Liste déroulante" },
  { id: "checkbox", label: "Case à cocher" },
  { id: "url",      label: "URL / Lien" },
];

const PDF_POSITIONS = [
  { id: "header",       label: "En-tête" },
  { id: "before_lines", label: "Avant les lignes" },
  { id: "after_lines",  label: "Après les lignes" },
  { id: "footer",       label: "Pied de page" },
];

const SUGGESTED = [
  { field_label: "N° bon de commande client", field_name: "po_number",          field_type: "text",     entity_types: ["facture", "devis", "bon_commande"] },
  { field_label: "Référence projet",           field_name: "project_ref",        field_type: "text",     entity_types: ["facture", "devis"] },
  { field_label: "Code analytique",            field_name: "analytics_code",     field_type: "text",     entity_types: ["facture", "devis", "bon_commande"] },
  { field_label: "Interlocuteur client",       field_name: "client_contact",     field_type: "text",     entity_types: ["facture", "devis", "client"] },
  { field_label: "Conditions particulières",   field_name: "special_conditions", field_type: "textarea", entity_types: ["facture", "devis", "bon_commande"] },
];

function emptyField(entity_type) {
  return {
    field_label: "", field_name: "", field_type: "text",
    field_options: [], is_required: false, show_on_pdf: true,
    pdf_position: "after_lines", entity_type,
  };
}

// ── Panneau de configuration des champs personnalisés ─────────────────────
function CustomFieldsPanel({ companyId }) {
  const [entityTab, setEntityTab]   = useState("facture");
  const [fields, setFields]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modal, setModal]           = useState(null); // null | { field } (field=null → create)
  const [form, setForm]             = useState(emptyField("facture"));
  const [optionsText, setOptionsText] = useState(""); // comma-sep for select
  const [saving, setSaving]         = useState(false);
  const [toast, setToast]           = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const all = await api.customFields.list({ company_id: companyId });
      setFields(all);
    } catch { setFields([]); }
    finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const fieldsForTab = fields
    .filter(f => f.entity_type === entityTab)
    .sort((a, b) => a.sort_order - b.sort_order);

  function openCreate() {
    const f = emptyField(entityTab);
    setForm(f);
    setOptionsText("");
    setModal({ field: null });
  }

  function openEdit(field) {
    setForm({ ...field });
    setOptionsText(Array.isArray(field.field_options) ? field.field_options.join(", ") : "");
    setModal({ field });
  }

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function saveField(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: companyId,
        entity_type: modal.field ? modal.field.entity_type : entityTab,
        field_options: form.field_type === "select"
          ? optionsText.split(",").map(s => s.trim()).filter(Boolean)
          : [],
      };
      if (modal.field?.id) {
        await api.customFields.update(modal.field.id, payload);
        showToast("Champ modifié");
      } else {
        await api.customFields.create(payload);
        showToast("Champ créé");
      }
      setModal(null);
      load();
    } catch (err) {
      showToast(err.message, "error");
    } finally { setSaving(false); }
  }

  async function deleteField(id) {
    if (!window.confirm("Supprimer ce champ ? Les valeurs existantes seront conservées.")) return;
    try {
      await api.customFields.delete(id);
      showToast("Champ supprimé");
      load();
    } catch (err) { showToast(err.message, "error"); }
  }

  async function move(field, dir) {
    const list = [...fieldsForTab];
    const idx  = list.findIndex(f => f.id === field.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= list.length) return;
    [list[idx], list[swap]] = [list[swap], list[idx]];
    const items = list.map((f, i) => ({ id: f.id, sort_order: i }));
    try {
      await api.customFields.reorder(items);
      load();
    } catch {}
  }

  async function addSuggested(s) {
    try {
      await api.customFields.create({
        company_id: companyId, entity_type: entityTab,
        field_name: s.field_name, field_label: s.field_label, field_type: s.field_type,
        is_required: 0, show_on_pdf: 1, pdf_position: "after_lines",
        sort_order: fields.filter(f => f.entity_type === entityTab).length,
      });
      showToast(`Champ "${s.field_label}" ajouté`);
      load();
    } catch (err) { showToast(err.message, "error"); }
  }

  const inp = { ...S.input, padding: "7px 10px" };
  const lbl = { fontSize: 12, color: "#4b5563", display: "block", marginBottom: 4 };

  const suggestedForTab = SUGGESTED.filter(s => s.entity_types.includes(entityTab)
    && !fields.some(f => f.entity_type === entityTab && f.field_name === s.field_name));

  return (
    <div>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 4px", color: "var(--text-primary)", fontSize: 16, fontWeight: 700 }}>Champs personnalisés</h3>
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Ajoutez des champs sur mesure à vos documents et fiches.</div>
      </div>

      {/* Onglets type d'entité */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid #d1d5db", paddingBottom: 1 }}>
        {ENTITY_TYPES.map(et => (
          <button key={et.id} onClick={() => setEntityTab(et.id)}
            style={{ ...S.btn, padding: "7px 14px", fontSize: 12, borderRadius: "8px 8px 0 0",
              background: entityTab === et.id ? "#1e40af" : "transparent",
              color: entityTab === et.id ? "#fff" : "#4b5563",
              borderBottom: entityTab === et.id ? "2px solid #3b82f6" : "2px solid transparent" }}>
            {et.label}
          </button>
        ))}
      </div>

      {/* Suggestions */}
      {suggestedForTab.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16, padding: "14px 16px" }}>
          <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 8, fontWeight: 600 }}>✨ Champs suggérés pour ce type de document</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {suggestedForTab.map(s => (
              <button key={s.field_name} onClick={() => addSuggested(s)}
                style={{ ...S.btn, padding: "5px 12px", fontSize: 12, background: "rgba(59,130,246,0.1)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.2)" }}>
                + {s.field_label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
        <button onClick={openCreate}
          style={{ ...S.btn, background: "#1e40af", color: "#fff", padding: "8px 18px" }}>
          + Ajouter un champ
        </button>
      </div>

      {/* Tableau des champs */}
      {loading ? (
        <div style={{ color: "#4b5563", textAlign: "center", padding: 30 }}>Chargement…</div>
      ) : fieldsForTab.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 32, color: "#4b5563" }}>
          Aucun champ personnalisé pour ce type de document.<br />
          <span style={{ fontSize: 12 }}>Cliquez sur "+ Ajouter un champ" pour commencer.</span>
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb" }}>
                {["Label", "Nom technique", "Type", "Requis", "Sur PDF", "Position PDF", "Ordre", ""].map((h, i) => (
                  <th key={i} style={{ padding: "10px 14px", fontSize: 11, color: "#475569", textAlign: "left", fontWeight: 600, borderBottom: "1px solid #d1d5db" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fieldsForTab.map((f, idx) => (
                <tr key={f.id} style={{ borderBottom: "1px solid #d1d5db" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(30,64,175,0.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td style={{ padding: "10px 14px", color: "#111827", fontSize: 13, fontWeight: 500 }}>{f.field_label}</td>
                  <td style={{ padding: "10px 14px", color: "#4b5563", fontSize: 12, fontFamily: "monospace" }}>{f.field_name}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: "rgba(59,130,246,0.1)", color: "#60a5fa" }}>
                      {FIELD_TYPES.find(t => t.id === f.field_type)?.label || f.field_type}
                    </span>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ color: f.is_required ? "#10b981" : "#475569", fontSize: 13 }}>{f.is_required ? "✓ Oui" : "Non"}</span>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <span style={{ color: f.show_on_pdf ? "#10b981" : "#475569", fontSize: 13 }}>{f.show_on_pdf ? "✓ Oui" : "Non"}</span>
                  </td>
                  <td style={{ padding: "10px 14px", color: "#4b5563", fontSize: 12 }}>
                    {PDF_POSITIONS.find(p => p.id === f.pdf_position)?.label || f.pdf_position}
                  </td>
                  <td style={{ padding: "10px 6px" }}>
                    <div style={{ display: "flex", gap: 2 }}>
                      <button onClick={() => move(f, -1)} disabled={idx === 0}
                        style={{ background: "none", border: "none", color: idx === 0 ? "#e5e7eb" : "#4b5563", cursor: idx === 0 ? "default" : "pointer", fontSize: 14, padding: "2px 4px" }}>▲</button>
                      <button onClick={() => move(f, 1)} disabled={idx === fieldsForTab.length - 1}
                        style={{ background: "none", border: "none", color: idx === fieldsForTab.length - 1 ? "#e5e7eb" : "#4b5563", cursor: idx === fieldsForTab.length - 1 ? "default" : "pointer", fontSize: 14, padding: "2px 4px" }}>▼</button>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => openEdit(f)}
                        style={{ ...S.btn, padding: "3px 10px", fontSize: 11, background: "rgba(59,130,246,0.1)", color: "#60a5fa" }}>Éditer</button>
                      <button onClick={() => deleteField(f.id)}
                        style={{ ...S.btn, padding: "3px 10px", fontSize: 11, background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>Suppr.</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal create/edit */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <form onSubmit={saveField} style={{ background: "#ffffff", border: "1px solid #21262d", borderRadius: 12, width: "100%", maxWidth: 520, padding: 28 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: "#111827", fontSize: 16 }}>
                {modal.field ? "Modifier le champ" : "Ajouter un champ"}
              </h3>
              <button type="button" onClick={() => setModal(null)} style={{ background: "none", border: "none", color: "#4b5563", fontSize: 22, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={lbl}>Label affiché *</label>
                <input style={inp} value={form.field_label} required onChange={e => {
                  set("field_label", e.target.value);
                  if (!modal.field) set("field_name", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "_").replace(/__+/g, "_").slice(0, 40));
                }} />
              </div>
              <div>
                <label style={lbl}>Nom technique</label>
                <input style={inp} value={form.field_name} onChange={e => set("field_name", e.target.value)}
                  pattern="[a-z0-9_]+" title="Lettres minuscules, chiffres, underscore" />
              </div>
              <div>
                <label style={lbl}>Type de champ</label>
                <select style={inp} value={form.field_type} onChange={e => set("field_type", e.target.value)}>
                  {FIELD_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {form.field_type === "select" && (
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Options (séparées par virgule)</label>
                <input style={inp} value={optionsText} placeholder="Option 1, Option 2, Option 3"
                  onChange={e => setOptionsText(e.target.value)} />
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Position sur le PDF</label>
                <select style={inp} value={form.pdf_position} onChange={e => set("pdf_position", e.target.value)}>
                  {PDF_POSITIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.is_required} onChange={e => set("is_required", e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: "#6366f1" }} />
                  <span style={{ color: "#111827", fontSize: 13 }}>Champ obligatoire</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                  <input type="checkbox" checked={!!form.show_on_pdf} onChange={e => set("show_on_pdf", e.target.checked)}
                    style={{ width: 15, height: 15, accentColor: "#6366f1" }} />
                  <span style={{ color: "#111827", fontSize: 13 }}>Afficher sur le PDF</span>
                </label>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setModal(null)}
                style={{ ...S.btn, background: "transparent", border: "1px solid #e5e7eb", color: "#4b5563" }}>Annuler</button>
              <button type="submit" disabled={saving}
                style={{ ...S.btn, background: "#1e40af", color: "#fff" }}>
                {saving ? "Enregistrement…" : modal.field ? "Modifier" : "Créer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ── Page principale Modules ───────────────────────────────────────────────
export default function Modules({ currentCompanyId, onNavigate }) {
  const [plan, setPlan]                 = useState(null);
  const [loading, setLoading]           = useState(true);
  const [toast, setToast]               = useState(null);
  const [view, setView]                 = useState("modules");
  const [company, setCompany]           = useState(null);
  const [savingMention, setSavingMention] = useState(false);

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api.billing.myPlan()
      .then(setPlan)
      .catch(() => showToast("Erreur de chargement de l'abonnement.", "error"))
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!currentCompanyId) return;
    api.companies.get(currentCompanyId).then(setCompany).catch(() => {});
  }, [currentCompanyId]);

  const toggleMention = async () => {
    const next = company?.show_execpro360_mention === 0 ? 1 : 0;
    setSavingMention(true);
    try {
      await api.companies.update(currentCompanyId, { show_execpro360_mention: next });
      setCompany(c => ({ ...c, show_execpro360_mention: next }));
    } catch (err) {
      showToast(err.message || "Erreur lors de la modification.", "error");
    } finally { setSavingMention(false); }
  };

  const activeModules = plan?.modules || [];

  if (loading) {
    return (
      <div style={{ padding: "0 0 40px", textAlign: "center", color: "#4b5563" }}>Chargement…</div>
    );
  }

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      {/* Navigation principale */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #d1d5db" }}>
        {[
          { id: "modules", label: "🧩 Mon forfait" },
          { id: "champs",  label: "⚙️ Champs personnalisés" },
        ].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{ ...S.btn, padding: "10px 20px", fontSize: 13, borderRadius: "8px 8px 0 0",
              background: view === t.id ? "#1e40af" : "transparent",
              color: view === t.id ? "#fff" : "#4b5563",
              borderBottom: view === t.id ? "2px solid #3b82f6" : "2px solid transparent" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Vue Champs personnalisés ──────────────── */}
      {view === "champs" && (
        <CustomFieldsPanel companyId={currentCompanyId} />
      )}

      {/* ── Vue Mon forfait — lecture seule, le changement se fait sur
          l'écran "Mon abonnement" (chantier "3 forfaits") ──────────── */}
      {view === "modules" && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>Mon forfait</h2>
              <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>Modules inclus dans votre forfait actuel</div>
            </div>
            <div style={{ background: "#ffffff", borderRadius: 10, padding: "10px 16px", textAlign: "right", border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#4b5563" }}>{plan?.plan_name || "Aucun forfait actif"}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1a9e72" }}>{plan?.totalMonthly ?? 0}€ HT/mois</div>
            </div>
          </div>

          {/* Réglage document — sans rapport avec le forfait, volontairement
              séparé pour ne pas laisser croire à un module payant. */}
          {company && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 20px", marginBottom: 20 }}>
              <span style={{ fontSize: 13, color: "#111827", fontWeight: 600 }}>Afficher la mention ExecPro360 sur mes factures</span>
              <div onClick={() => !savingMention && toggleMention()} style={{ width: 44, height: 24, borderRadius: 12, background: company.show_execpro360_mention !== 0 ? "#1a9e72" : "#e5e7eb", position: "relative", cursor: savingMention ? "wait" : "pointer", transition: "background 0.2s", flexShrink: 0, opacity: savingMention ? 0.6 : 1 }}>
                <div style={{ position: "absolute", top: 2, left: company.show_execpro360_mention !== 0 ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
              </div>
            </div>
          )}

          {activeModules.length === 0 ? (
            <div style={{ ...S.card, textAlign: "center", padding: 32, color: "#4b5563" }}>
              Votre forfait actuel n'inclut pas de module en plus du socle Essentiel (facturation, comptabilité/FEC, trésorerie, relances, accès expert-comptable, lecture automatique des factures PDF par IA).
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
              {activeModules.map(key => {
                const mod = MODULE_CATALOG[key];
                if (!mod) return null;
                return (
                  <div key={key} style={{ ...S.card, border: "1px solid rgba(26,158,114,0.3)", background: "rgba(26,158,114,0.05)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <span style={{ fontSize: 24 }}>{mod.ico}</span>
                      <div style={{ color: "#111827", fontWeight: 700, fontSize: 13 }}>{mod.name}</div>
                    </div>
                    <div style={{ color: "#4b5563", fontSize: 12, lineHeight: 1.5 }}>{mod.desc}</div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ ...S.card, marginTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Besoin d'un autre forfait ?</div>
              <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>Changez de forfait ou ajoutez des sociétés satellites depuis "Mon abonnement".</div>
            </div>
            <button onClick={() => onNavigate?.("monabonnement")} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>Gérer l'abonnement</button>
          </div>
        </>
      )}
    </div>
  );
}

